<script>
import Swal from 'sweetalert2';
import VButton from '@/components/Button.vue';

const COUNTDOWN_SECONDS = 60;
const CODE_LENGTH = 6;

export default {
  name: 'TwoFactorAuthModal',
  components: {
    VButton
  },
  props: {
    emailToCode: {
      type: String,
      default: ''
    },
    phoneNumber: {
      type: String,
      default: ''
    },
    loginValue: {
      type: String,
      required: true
    },
    password: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      step: 'contact',
      selectedMethod: 'email',
      haveACode: false,
      secondsLeft: 0,
      countdownTimer: null,
      digits: ['', '', '', '', '', ''],
      sending: false,
      confirming: false
    };
  },
  computed: {
    selectedContact() {
      return this.selectedMethod === 'phone'
        ? this.phoneNumber
        : this.emailToCode;
    },
    codeValue() {
      return this.digits.join('');
    },
    isCodeComplete() {
      return this.codeValue.length === CODE_LENGTH;
    },
    isBusy() {
      return this.sending || this.confirming;
    }
  },
  created() {
    if (!this.emailToCode && this.phoneNumber) {
      this.selectedMethod = 'phone';
    }
  },
  mounted() {
    document.body.classList.add('no-scroll');
  },
  beforeDestroy() {
    this.clearCountdown();
    document.body.classList.remove('no-scroll');
  },
  methods: {
    loginPayload(extra) {
      return Object.assign(
        {
          email: this.loginValue,
          senha: this.password
        },
        extra || {}
      );
    },
    showExpiredPassword(email) {
      Swal.fire({
        title: 'Sua senha está expirada',
        text: `Sua senha expirou! Para sua segurança redefina sua senha seguindo o passo-a-passo enviado para o e-mail ${email} ou entre em contato com a nossa central de ajuda.`,
        type: 'info',
        confirmButtonText: 'Ok'
      });
      this.handleCancel();
    },
    clearToast() {
      this.$store.commit('message', { type: 'error', text: false });
    },
    showInvalidCode(text) {
      this.clearToast();
      Swal.fire({
        title: 'Código Inválido',
        html:
          text
          || 'Seu código pode estar expirado ou inválido. <br> Clique no botão para tentar novamente',
        type: 'error',
        confirmButtonText: 'Ok'
      });
    },
    handleCancel() {
      this.clearCountdown();
      this.$emit('cancel');
    },
    goToCodeStep(startTimer) {
      this.step = 'code';
      this.digits = ['', '', '', '', '', ''];
      if (startTimer) {
        this.haveACode = false;
        this.startCountdown();
      } else {
        this.haveACode = true;
        this.clearCountdown();
        this.secondsLeft = 0;
      }
      this.$nextTick(() => {
        this.focusInput(0);
      });
    },
    handleHasACode() {
      if (this.isBusy) return;
      this.goToCodeStep(false);
    },
    handleBackToContact() {
      if (this.isBusy) return;
      this.clearCountdown();
      this.haveACode = false;
      this.digits = ['', '', '', '', '', ''];
      this.step = 'contact';
    },
    startCountdown() {
      this.clearCountdown();
      this.secondsLeft = COUNTDOWN_SECONDS;
      this.countdownTimer = setInterval(() => {
        if (this.secondsLeft <= 1) {
          this.secondsLeft = 0;
          this.clearCountdown();
        } else {
          this.secondsLeft -= 1;
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    sendCode() {
      if (this.isBusy) return;
      if (!this.selectedMethod) {
        Swal.fire({
          title: 'Seleção obrigatória',
          text:
            'É obrigatório a seleção de uma das opções para receber o código de login. Tente novamente, por favor.',
          type: 'info',
          confirmButtonText: 'Ok'
        });
        return;
      }
      this.sending = true;
      this.$store.commit('formLoading', true);
      this.$http
        .post(
          `${process.env.VUE_APP_HUB_BASE_URL2}/users/login`,
          this.loginPayload({ selectMethod: this.selectedMethod })
        )
        .then(({ data }) => {
          if (data.emailToCode || data.phoneNumber) {
            this.goToCodeStep(true);
          } else if (data.message && !data.emailToCode && !data.phoneNumber) {
            this.showExpiredPassword(data.email);
          } else if (!data.ok) {
            this.showInvalidCode();
          }
        })
        .catch(err => {
          this.clearToast();
          Swal.fire({
            title: 'Não foi possível enviar o código',
            text: err && err.message ? err.message : 'Tente novamente.',
            type: 'error',
            confirmButtonText: 'Ok'
          });
        })
        .then(() => {
          this.sending = false;
          this.$store.commit('formLoading', false);
        });
    },
    resendCode() {
      if (this.isBusy || this.secondsLeft > 0) return;
      this.sendCode();
    },
    confirmCode() {
      if (this.isBusy || !this.isCodeComplete) return;
      this.confirming = true;
      this.$store.commit('formLoading', true);
      this.$http
        .post(
          `${process.env.VUE_APP_HUB_BASE_URL2}/users/login`,
          this.loginPayload({ code: this.codeValue })
        )
        .then(({ data }) => {
          if (data.token) {
            this.clearCountdown();
            this.$emit('success', data.token);
          } else if (data.message && !data.emailToCode) {
            this.showExpiredPassword(data.email);
          } else if (!data.ok) {
            this.showInvalidCode();
          }
        })
        .catch(err => {
          this.showInvalidCode(
            err && err.message ? err.message : 'Tente novamente.'
          );
        })
        .then(() => {
          this.confirming = false;
          this.$store.commit('formLoading', false);
        });
    },
    focusInput(index) {
      const inputs = this.$refs.codeInputs;
      if (!inputs) return;
      const input = Array.isArray(inputs) ? inputs[index] : inputs;
      if (input && input.focus) input.focus();
    },
    applyDigits(chars, startIndex) {
      const next = this.digits.slice();
      chars.forEach((char, offset) => {
        const index = startIndex + offset;
        if (index < CODE_LENGTH) {
          next[index] = char;
        }
      });
      this.digits = next;
      const filled = Math.min(startIndex + chars.length, CODE_LENGTH - 1);
      this.$nextTick(() => this.focusInput(filled));
    },
    onDigitInput(index, event) {
      const raw = (event.target.value || '').replace(/\D/g, '');
      if (raw.length > 1) {
        this.applyDigits(raw.slice(0, CODE_LENGTH).split(''), index);
        return;
      }
      const value = raw.slice(-1);
      event.target.value = value;
      const next = this.digits.slice();
      next[index] = value;
      this.digits = next;
      if (value && index < CODE_LENGTH - 1) {
        this.focusInput(index + 1);
      }
    },
    onDigitKeydown(index, event) {
      if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
        event.preventDefault();
        const next = this.digits.slice();
        next[index - 1] = '';
        this.digits = next;
        this.focusInput(index - 1);
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        this.focusInput(index - 1);
      }
      if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
        event.preventDefault();
        this.focusInput(index + 1);
      }
      if (event.key === 'Enter') {
        this.confirmCode();
      }
    },
    onDigitPaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData.getData('text') || '')
        .replace(/\D/g, '')
        .slice(0, CODE_LENGTH);
      if (!pasted) return;
      this.applyDigits(pasted.split(''), 0);
    }
  }
};
</script>

<template>
  <div class="two-factor" role="dialog" aria-modal="true">
    <div class="two-factor__overlay" />
    <div class="two-factor__card">
      <form
        v-if="step === 'contact'"
        class="two-factor__form"
        @submit.prevent="sendCode"
      >
        <p class="two-factor__title">
          Selecione um contato para receber o código de autenticação.
        </p>
        <div class="two-factor__options">
          <label v-if="emailToCode" class="two-factor__option">
            <input
              v-model="selectedMethod"
              type="radio"
              name="two-factor-method"
              value="email"
            />
            <span>{{ emailToCode }}</span>
          </label>
          <label v-if="phoneNumber" class="two-factor__option">
            <input
              v-model="selectedMethod"
              type="radio"
              name="two-factor-method"
              value="phone"
            />
            <span>{{ phoneNumber }}</span>
          </label>
        </div>
        <v-button
          type="button"
          class="two-factor__submit"
          bgColor="blue"
          :radius="true"
          :rebranding="true"
          :disabled="isBusy"
          label="Enviar código"
          @click="sendCode"
        />
        <div class="two-factor__footer">
          <button
            type="button"
            class="two-factor__link"
            @click="handleCancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="two-factor__link"
            @click="handleHasACode"
          >
            Já tem um código? Clique aqui.
          </button>
        </div>
      </form>

      <form
        v-else
        class="two-factor__form"
        @submit.prevent="confirmCode"
      >
        <div class="two-factor__code-header">
          <p class="two-factor__title">Insira o código enviado para:</p>
          <p class="two-factor__contact">{{ selectedContact }}</p>
        </div>
        <div class="two-factor__otp" @paste="onDigitPaste">
          <input
            v-for="(digit, index) in digits"
            :key="index"
            ref="codeInputs"
            :value="digit"
            class="two-factor__otp-input"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="1"
            autocomplete="one-time-code"
            autocapitalize="off"
            autocorrect="off"
            @input="onDigitInput(index, $event)"
            @keydown="onDigitKeydown(index, $event)"
          />
        </div>
        <div v-if="!haveACode" class="two-factor__timer">
          <p v-if="secondsLeft > 0" class="two-factor__timer-text">
            Aguarde {{ secondsLeft }} segundo(s) para reenviar.
          </p>
          <button
            v-else
            type="button"
            class="two-factor__link"
            @click="resendCode"
          >
            Reenviar código
          </button>
        </div>
        <v-button
          type="button"
          class="two-factor__submit"
          bgColor="blue"
          :radius="true"
          :rebranding="true"
          :disabled="isBusy || !isCodeComplete"
          label="Confirmar"
          @click="confirmCode"
        />
        <div class="two-factor__footer">
          <button
            type="button"
            class="two-factor__link"
            @click="handleCancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="two-factor__link"
            @click="handleBackToContact"
          >
            Voltar
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.two-factor {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;

  &__overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.45);
  }

  &__card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 28.5rem;
    background: var(--color-white);
    border-radius: 0.5rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    padding: 2rem 1.75rem 1.5rem;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__title {
    color: var(--color-primary);
    font-size: 1rem;
    line-height: 1.5;
    font-weight: 500;
  }

  &__contact {
    color: var(--color-primary);
    font-size: 0.95rem;
    margin-top: 0.25rem;
  }

  &__code-header {
    margin-bottom: 0.5rem;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--color-primary);
    font-size: 0.95rem;
    cursor: pointer;

    input {
      accent-color: var(--color-primary);
      cursor: pointer;
      width: 1.1rem;
      height: 1.1rem;
    }
  }

  &__otp {
    display: flex;
    justify-content: space-between;
    gap: 0.4rem;
  }

  &__otp-input {
    flex: 1;
    max-width: 3.5rem;
    height: 3.5rem;
    border: 1px solid var(--color-grey-light);
    border-radius: 0.375rem;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-primary);
    background: var(--color-white);
    user-select: text;
    caret-color: var(--color-primary);

    &:focus {
      border-color: var(--color-primary);
    }
  }

  &__timer {
    display: flex;
    justify-content: flex-end;
    min-height: 1.25rem;
  }

  &__timer-text {
    color: var(--color-grey-medium);
    font-size: 0.8125rem;
  }

  &__submit {
    margin-top: 0.25rem;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }

  &__link {
    background: none;
    border: none;
    padding: 0;
    color: var(--color-grey-medium);
    font-size: 0.8125rem;
    font-family: inherit;
    font-weight: 400;
    line-height: 1.4;
    height: auto;
    text-transform: none;
    cursor: pointer;
    text-align: left;
    min-width: auto;
    box-shadow: none;

    &:last-child {
      text-align: right;
    }

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
